// src/context/CartContext.jsx
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'sohocard.cart';

/* Kinds that allow quantity > 1 */
const QTY_KINDS = new Set(['gift_card', 'virtual_card']);

/* --------------------------------------------------------------
   Cart line shape (stored in localStorage):

   {
     id,               // `${productId}::${amount}` or productId
     productId,        // UUID
     sku,
     title,
     kind,
     currency,         // always 'IRR' now
     unitPrice,        // IRR per line (before qty)
     qty,
     region,
     brandName,
     brandColor,
     brandLogo,
     destination,      // { destination_id, provider, kind, destination } | null
     native_currency,
     native_amount,
     fx_rate,
   }
-------------------------------------------------------------- */

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch { /* quota */ }
    }, [items]);

    /* ---------------- normalize incoming product ---------------- */
    const normalize = (product, qty) => {
        const productId = product.product_id ?? product.productId ?? product.id;
        const amount = product.amount != null ? Number(product.amount) : null;

        const lineKey =
            product.id && String(product.id).includes('::')
                ? product.id
                : amount != null
                    ? `${productId}::${amount}`
                    : productId;

        const kind = product.kind || 'gift_card';
        const isQtyKind = QTY_KINDS.has(kind);

        /* unit price resolution */
        let unitPrice = 0;
        if (product.unit_price != null) unitPrice = Number(product.unit_price);
        else if (product.price != null) unitPrice = Number(product.price);

        return {
            id: lineKey,
            productId,
            sku: product.sku || '',
            title: product.title || '',
            kind,
            currency: product.currency || 'IRR',
            unitPrice,
            amount,
            qty: isQtyKind ? qty : 1,
            region: product.region || null,

            brandName: product.brands?.name || product.brandName || null,
            brandColor: product.brands?.bg_color || product.brandColor || null,
            brandLogo: product.brands?.logo_url || product.brandLogo || null,

            destination: product.destination || null,

            /* native reference fields */
            native_currency: product.native_currency || product.currency || 'USD',
            native_amount: product.native_amount != null ? Number(product.native_amount) : null,
            fx_rate: product.fx_rate != null ? Number(product.fx_rate) : null,
        };
    };

    /* ---------------- add ---------------- */
    const add = useCallback((product, qty = 1) => {
        if (!product) return;

        const productId = product.product_id ?? product.productId ?? product.id;
        if (!productId) {
            // eslint-disable-next-line no-console
            console.warn('[CartContext.add] missing product id');
            return;
        }

        const isQtyKind = QTY_KINDS.has(product.kind);
        const safeQty = isQtyKind
            ? Math.max(1, Math.min(10, Number(qty) || 1))
            : 1;

        const nextItem = normalize(product, safeQty);

        setItems((prev) => {
            const existing = prev.find(
                (x) =>
                    x.productId === nextItem.productId &&
                    x.amount === nextItem.amount &&
                    (x.destination?.destination || null) ===
                    (nextItem.destination?.destination || null)
            );

            if (existing) {
                const canQty = QTY_KINDS.has(existing.kind);
                return prev.map((x) =>
                    x.id === existing.id
                        ? {
                            ...x,
                            qty: canQty
                                ? Math.min(x.qty + safeQty, 10)
                                : 1,
                        }
                        : x
                );
            }

            return [...prev, nextItem];
        });
    }, []);

    /* ---------------- remove ---------------- */
    const remove = useCallback((lineId) => {
        setItems((prev) => prev.filter((x) => x.id !== lineId));
    }, []);

    /* ---------------- setQty ---------------- */
    const setQty = useCallback((lineId, qty) => {
        setItems((prev) =>
            prev.map((x) => {
                if (x.id !== lineId) return x;
                if (!QTY_KINDS.has(x.kind)) return { ...x, qty: 1 };
                const safe = Math.max(1, Math.min(10, Number(qty) || 1));
                return { ...x, qty: safe };
            })
        );
    }, []);

    const increment = useCallback((lineId, by = 1) => {
        setItems((prev) =>
            prev.map((x) => {
                if (x.id !== lineId) return x;
                if (!QTY_KINDS.has(x.kind)) return x;
                return { ...x, qty: Math.min(10, x.qty + by) };
            })
        );
    }, []);

    const decrement = useCallback((lineId, by = 1) => {
        setItems((prev) =>
            prev.map((x) => {
                if (x.id !== lineId) return x;
                if (!QTY_KINDS.has(x.kind)) return x;
                return { ...x, qty: Math.max(1, x.qty - by) };
            })
        );
    }, []);

    /* ---------------- clear ---------------- */
    const clear = useCallback(() => setItems([]), []);

    /* ---------------- derived totals ---------------- */
    const totals = useMemo(() => {
        const subtotal = items.reduce(
            (s, x) => s + Number(x.unitPrice || 0) * Number(x.qty || 0),
            0
        );
        const itemCount = items.reduce((s, x) => s + Number(x.qty || 0), 0);
        const uniqueCount = items.length;
        const currency = items[0]?.currency || 'IRR';
        const hasMixedCurrency = items.some((x) => x.currency !== currency);

        return { subtotal, itemCount, uniqueCount, currency, hasMixedCurrency };
    }, [items]);

    /* ---------------- helpers ---------------- */
    const has = useCallback(
        (productId) =>
            items.some(
                (x) =>
                    x.productId === productId ||
                    (x.id && String(x.id).startsWith(`${productId}::`))
            ),
        [items]
    );

    const getItem = useCallback(
        (lineId) => items.find((x) => x.id === lineId) || null,
        [items]
    );

    const value = {
        items,
        totals,
        count: totals.itemCount,
        subtotal: totals.subtotal,
        currency: totals.currency,

        add,
        remove,
        setQty,
        increment,
        decrement,
        clear,

        has,
        getItem,
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
    return ctx;
}